/**
 * CommonCodeItemCard Component
 * 개별 공통코드 입력 카드
 */
import React from 'react';
import FormInput from '@/shared/components/forms/FormInput';
import PrimaryButton from '@/shared/components/buttons/PrimaryButton';
import { CommonCodeFormItem, MAX_VALUE_FIELDS } from './commonCodeCreateHelpers';

// ======== 타입 ========

interface CommonCodeItemCardProps {
    code: CommonCodeFormItem;
    index: number;
    canDelete: boolean;
    onChange: (id: string, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onRemove: (id: string) => void;
    onToggleValueField: (id: string, fieldIndex: number) => void;
}

// ======== 서브 컴포넌트 ========

/**
 * 코드값 필드 컴포넌트
 */
const ValueField: React.FC<{
    code: CommonCodeFormItem;
    fieldNum: number;
    onChange: (id: string, e: React.ChangeEvent<HTMLInputElement>) => void;
    onToggle: (id: string, fieldIndex: number) => void;
}> = ({ code, fieldNum, onChange, onToggle }) => {
    const fieldName = `val${fieldNum}` as keyof CommonCodeFormItem;
    const value = code[fieldName] as string;

    return (
        <div className="form-group form-group-with-delete">
            <FormInput
                id={`${code.id}-val${fieldNum}`}
                name={`val${fieldNum}`}
                type="text"
                label={`코드값${fieldNum}`}
                value={value}
                onChange={(e) => onChange(code.id, e)}
                placeholder={`코드값${fieldNum}을 입력하세요`}
            />
            <button
                type="button"
                onClick={() => onToggle(code.id, fieldNum)}
                className="delete-field-button"
                title={`코드값${fieldNum} 삭제`}
            >
                <i className="fas fa-times"></i>
            </button>
        </div>
    );
};

// ======== 메인 컴포넌트 ========

const CommonCodeItemCard: React.FC<CommonCodeItemCardProps> = ({
                                                                   code,
                                                                   index,
                                                                   canDelete,
                                                                   onChange,
                                                                   onRemove,
                                                                   onToggleValueField,
                                                               }) => {
    return (
        <div className="common-code-item">
            {/* 헤더 */}
            <div className="common-code-item-header">
                <h3 className="common-code-item-title">공통코드 {index + 1}</h3>
                {canDelete && (
                    <button
                        type="button"
                        onClick={() => onRemove(code.id)}
                        className="delete-common-code-button"
                        title="공통코드 삭제"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                )}
            </div>

            {/* 공통코드/공통코드명 */}
            <div className="form-row">
                <div className="form-group">
                    <FormInput
                        id={`${code.id}-comCode`}
                        name="comCode"
                        type="text"
                        label="공통코드"
                        value={code.comCode}
                        onChange={(e) => onChange(code.id, e)}
                        placeholder="공통코드를 입력하세요"
                        required
                    />
                </div>
                <div className="form-group">
                    <FormInput
                        id={`${code.id}-comCdName`}
                        name="comCdName"
                        type="text"
                        label="공통코드명"
                        value={code.comCdName}
                        onChange={(e) => onChange(code.id, e)}
                        placeholder="공통코드명을 입력하세요"
                        required
                    />
                </div>
            </div>

            {/* 순번/사용여부 */}
            <div className="form-row">
                <div className="form-group">
                    <label htmlFor={`${code.id}-comSeqno`} className="form-label">
                        공통코드순번
                    </label>
                    <input
                        type="number"
                        id={`${code.id}-comSeqno`}
                        name="comSeqno"
                        className="form-input"
                        value={code.comSeqno}
                        onChange={(e) => onChange(code.id, e)}
                        placeholder="공통코드순번을 입력하세요"
                        autoComplete="off"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${code.id}-useYn`} className="form-label">
                        사용여부
                    </label>
                    <select
                        id={`${code.id}-useYn`}
                        name="useYn"
                        value={code.useYn}
                        onChange={(e) => onChange(code.id, e)}
                        className="form-select"
                        autoComplete="off"
                    >
                        <option value="Y">사용</option>
                        <option value="N">미사용</option>
                    </select>
                </div>
            </div>

            {/* 코드값 필드들 - Row 1 (val1, val2) */}
            {code.visibleValueFields >= 1 && (
                <div className="form-row">
                    <ValueField code={code} fieldNum={1} onChange={onChange} onToggle={onToggleValueField} />
                    {code.visibleValueFields >= 2 && (
                        <ValueField code={code} fieldNum={2} onChange={onChange} onToggle={onToggleValueField} />
                    )}
                </div>
            )}

            {/* 코드값 필드들 - Row 2 (val3, val4) */}
            {code.visibleValueFields >= 3 && (
                <div className="form-row">
                    <ValueField code={code} fieldNum={3} onChange={onChange} onToggle={onToggleValueField} />
                    {code.visibleValueFields >= 4 && (
                        <ValueField code={code} fieldNum={4} onChange={onChange} onToggle={onToggleValueField} />
                    )}
                </div>
            )}

            {/* 코드값 필드들 - Row 3 (val5) */}
            {code.visibleValueFields >= 5 && (
                <div className="form-row">
                    <ValueField code={code} fieldNum={5} onChange={onChange} onToggle={onToggleValueField} />
                </div>
            )}

            {/* 코드값 추가 버튼 */}
            {code.visibleValueFields < MAX_VALUE_FIELDS && (
                <div className="form-row">
                    <div className="form-group add-value-button-wrapper">
                        <PrimaryButton
                            type="button"
                            onClick={() => onToggleValueField(code.id, code.visibleValueFields)}
                            showArrow={false}
                            className="add-value-button"
                        >
                            <i className="fas fa-plus"></i>
                            코드값 추가
                        </PrimaryButton>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CommonCodeItemCard;
